/* First-visit email signup card and its bottom-left $15 button.
 * Non-modal: no overlay, no scroll lock. The card opens by itself once the page's hero
 * has been scrolled past, and closing it in any way shrinks it into the button, which reopens it.
 * "No thanks" stops the automatic opening for good; subscribers never see either. */
(() => {
  'use strict';
  if (window.torqueSignupPopup) return;
  window.torqueSignupPopup = true;

  const popup = document.querySelector('[data-tq-signup-popup]');
  if (!popup) return;
  const tab = document.querySelector('[data-tq-popup-tab]');
  const tabButton = tab?.querySelector('[data-tq-popup-open]');
  const form = popup.querySelector('form');
  const result = popup.querySelector('[data-tq-popup-result]')?.dataset.tqPopupResult;
  const root = document.documentElement;

  const neverKey = 'tq-signup-popup-never';
  const snoozeKey = 'tq-signup-popup-snoozed';
  const subscriberKey = 'tq-signup-subscribed';
  const tabDismissKey = 'tq-signup-tab-dismissed';
  const submittedKey = 'tq-signup-popup-submitted';
  const screens = Math.min(2, Math.max(0.25, Number(popup.dataset.scroll || 75) / 100));
  const delayMs = Math.max(0, Number(popup.dataset.delay || 5)) * 1000;
  const snoozeDays = Math.max(1, Number(popup.dataset.snoozeDays || 7));
  const animationMs = 320; // matches the slowest (phone sheet) transition
  let closeTimer = 0;

  function writeFlag(key, days) {
    const expires = String(Date.now() + days * 86400000);
    try { localStorage.setItem(key, expires); } catch {}
    document.cookie = `${key}=${expires}; Max-Age=${Math.round(days * 86400)}; Path=/; SameSite=Lax`;
  }
  function readFlag(key) {
    try {
      const stored = Number(localStorage.getItem(key));
      if (Number.isFinite(stored) && stored > Date.now()) return true;
    } catch {}
    const match = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`));
    const value = match ? Number(decodeURIComponent(match[1])) : 0;
    return Number.isFinite(value) && value > Date.now();
  }
  function knownSubscriber() {
    try {
      const match = document.cookie.match(/(?:^|; )__kla_id=([^;]*)/);
      if (!match) return false;
      const raw = decodeURIComponent(match[1]);
      const parsed = JSON.parse(atob(raw.includes('.') ? raw : raw.replace(/-/g, '+')));
      return Boolean(parsed?.$email || parsed?.email);
    } catch {
      return false;
    }
  }
  function takeSubmitted() {
    try {
      const value = sessionStorage.getItem(submittedKey);
      sessionStorage.removeItem(submittedKey);
      return value === '1';
    } catch {
      return false;
    }
  }
  const subscribed = () => readFlag(subscriberKey) || knownSubscriber();
  const tabAllowed = () => Boolean(tab) && !subscribed() && !readFlag(tabDismissKey);
  const isOpen = () => !popup.hidden;

  // The page's hero: the first sizable block in the main content, looking inside any
  // wrapper that holds the whole page. Works across templates without per-page setup.
  let heroEl;
  function findHero() {
    const main = document.querySelector('main#MainContent') || document.querySelector('main');
    let scope = main;
    for (let depth = 0; scope && depth < 5; depth += 1) {
      const block = [...scope.children].find(el => {
        if (/^(LINK|SCRIPT|STYLE|TEMPLATE|NOSCRIPT)$/.test(el.tagName) || el.offsetHeight < 200) return false;
        const position = getComputedStyle(el).position;
        return position !== 'fixed' && position !== 'sticky';
      });
      if (!block) return null;
      if (block.offsetHeight > window.innerHeight * 1.6 && block.children.length) { scope = block; continue; }
      // A block that is most of the page is the content itself, not a hero.
      if (block.offsetHeight > window.innerHeight * 1.6 || block.offsetHeight >= main.offsetHeight * 0.8) return null;
      return block;
    }
    return null;
  }
  function heroPassed() {
    if (heroEl === undefined || (heroEl && !heroEl.isConnected)) heroEl = findHero();
    const header = parseFloat(getComputedStyle(root).getPropertyValue('--torque-v6-header-height')) || 80;
    const scrollable = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    if (heroEl) {
      const needed = heroEl.getBoundingClientRect().bottom + window.scrollY - header;
      // Short pages may never scroll the hero fully away; then most of the way down counts.
      return window.scrollY >= Math.min(needed, scrollable * 0.6);
    }
    // No clear hero (short or single-block pages): about one screen, or most of the way down.
    return window.scrollY >= Math.min(window.innerHeight * screens, scrollable * 0.6);
  }

  function blockedByOtherUi() {
    return root.classList.contains('mobile-menu-open')
      || document.body.classList.contains('klaviyo-prevent-body-scrolling')
      || Boolean(document.querySelector('.cart-drawer[open], [data-cart-drawer][open], dialog[open]'));
  }

  function showTab() {
    if (!tabAllowed()) return;
    tab.hidden = false;
    void tab.offsetWidth; // paint the start state so the button pops in
    tab.classList.add('is-visible');
  }
  function hideTab() {
    if (!tab) return;
    tab.classList.remove('is-visible');
    tab.hidden = true;
  }

  // Phones: float the card just below whatever is pinned to the top (navbar, quick-buy strips).
  const compact = window.matchMedia('(max-width: 720px)');
  function placeUnderHeader() {
    if (!compact.matches) return;
    let edge = 0;
    document.querySelectorAll('.ticker, header.site, #buybar, #mcta').forEach(el => {
      const box = el.getBoundingClientRect();
      if (box.height && box.top < window.innerHeight / 3 && box.bottom > edge && getComputedStyle(el).visibility !== 'hidden') edge = box.bottom;
    });
    root.style.setProperty('--tq-popup-top', `${Math.max(0, Math.round(edge))}px`);
  }
  let placeTicking = false;
  window.addEventListener('scroll', () => {
    if (popup.hidden || placeTicking) return;
    placeTicking = true;
    window.setTimeout(() => { placeTicking = false; placeUnderHeader(); }, 100);
  }, { passive: true });
  window.addEventListener('resize', () => { if (!popup.hidden) placeUnderHeader(); });

  function openPopup({ fromTab = false } = {}) {
    window.clearTimeout(closeTimer);
    root.classList.remove('tq-popup-pending');
    hideTab();
    tabButton?.setAttribute('aria-expanded', 'true');
    placeUnderHeader();
    popup.hidden = false;
    popup.classList.remove('is-visible');
    void popup.offsetWidth; // paint the shrunk start state, then grow
    popup.classList.add('is-visible');
    // Focus the card, not a field, so phones don't pop the keyboard open.
    if (fromTab) popup.focus({ preventScroll: true });
  }

  function closePopup({ never = false, toTab = true } = {}) {
    if (popup.hidden || !popup.classList.contains('is-visible')) return;
    writeFlag(never ? neverKey : snoozeKey, never ? 3650 : snoozeDays);
    const hadFocus = popup.contains(document.activeElement);
    popup.classList.remove('is-visible');
    tabButton?.setAttribute('aria-expanded', 'false');
    root.classList.remove('tq-popup-pending');
    closeTimer = window.setTimeout(() => {
      popup.hidden = true;
      if (toTab) {
        showTab();
        if (hadFocus && !tab?.hidden) tabButton?.focus({ preventScroll: true });
      }
    }, animationMs);
  }

  // Start-up state.
  if (takeSubmitted() && result) {
    // Back from this card's own submission: show the outcome.
    if (result === 'success') writeFlag(subscriberKey, 400);
    openPopup();
    if (result === 'success') {
      window.setTimeout(() => closePopup({ toTab: false }), 6000);
    }
  } else if (subscribed()) {
    // Nothing to show.
  } else if (!(readFlag(neverKey) || readFlag(snoozeKey))) {
    // First visit: the button waits until the card has had its turn.
    root.classList.add('tq-popup-pending');
    const start = Date.now();
    let armed = true;
    const check = () => {
      if (!armed) return;
      if (!heroPassed()) return;
      if (Date.now() - start < delayMs) {
        window.setTimeout(check, delayMs - (Date.now() - start) + 50);
        return;
      }
      if (blockedByOtherUi()) {
        window.setTimeout(check, 3000);
        return;
      }
      armed = false;
      window.removeEventListener('scroll', onScroll);
      openPopup();
    };
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.setTimeout(() => { ticking = false; check(); }, 120);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // Pages opened mid-way (anchor links, restored scroll) may already be past the hero.
    window.addEventListener('load', () => window.setTimeout(check, 300), { once: true });
  } else {
    showTab();
  }

  form?.addEventListener('submit', () => {
    try { sessionStorage.setItem(submittedKey, '1'); } catch {}
  });

  popup.addEventListener('click', event => {
    if (event.target.closest('[data-tq-popup-never]')) closePopup({ never: true });
    else if (event.target.closest('[data-tq-popup-close]')) closePopup();
  });
  tab?.addEventListener('click', event => {
    if (event.target.closest('[data-tq-popup-tab-dismiss]')) {
      writeFlag(tabDismissKey, 14);
      hideTab();
    } else if (event.target.closest('[data-tq-popup-open]')) {
      openPopup({ fromTab: true });
    }
  });
  // A tap or click outside the card closes it into the button. Scrolling the page
  // (a swipe that moves the finger or the page) leaves it open.
  let outsidePress = null;
  document.addEventListener('pointerdown', event => {
    outsidePress = null;
    if (!isOpen() || popup.contains(event.target) || tab?.contains(event.target)) return;
    outsidePress = { id: event.pointerId, x: event.clientX, y: event.clientY, scrollY: window.scrollY };
  });
  document.addEventListener('pointercancel', () => { outsidePress = null; });
  document.addEventListener('pointerup', event => {
    const press = outsidePress;
    outsidePress = null;
    if (!press || press.id !== event.pointerId || !isOpen()) return;
    const moved = Math.hypot(event.clientX - press.x, event.clientY - press.y);
    if (moved > 10 || Math.abs(window.scrollY - press.scrollY) > 4) return;
    closePopup();
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && isOpen()) closePopup();
  });
})();
